import React from 'react'
import { Box, Typography, useTheme } from '@material-ui/core'
import { ResponsiveLine } from '@nivo/line'
import { ResponsiveBar } from '@nivo/bar'
import { styled, alpha } from '@material-ui/core/styles'

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}))

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  textAlign: 'center',
}))

const NoDataMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
}))

const UploadTrendsChart = ({ data, chartType = 'line', height = 200 }) => {
  const theme = useTheme()
  const containerRef = React.useRef(null)
  const [tooltipState, setTooltipState] = React.useState({
    visible: false,
    left: 0,
    top: 0,
    dateLabel: '',
    countValue: 0,
  })

  if (!data || !data.daily_trends || data.daily_trends.length === 0) {
    return (
      <ChartContainer>
        <NoDataMessage>No upload data available for the selected period</NoDataMessage>
      </ChartContainer>
    )
  }

  // Format the data for the chart with better date formatting
  const chartData = [
    {
      id: 'uploads',
      data: data.daily_trends.map((item) => ({
        // Short, compact label for axis to avoid overlap
        x: new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        y: item.count,
        fullDate: new Date(item.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
        }),
      })),
    },
  ]

  // Dynamically compute tick density for readability (target ~7 ticks)
  const allXAxisValues = chartData[0].data.map((d) => d.x)
  const targetTickCount = 7
  const tickStep = Math.max(1, Math.ceil(allXAxisValues.length / targetTickCount))
  const xAxisTickValues = allXAxisValues.filter((_, index) => index % tickStep === 0)

  const chartTheme = {
    background: 'transparent',
    text: {
      fill: theme.palette.text.secondary,
      fontSize: 12,
      fontFamily: theme.typography.fontFamily,
    },
    axis: {
      domain: {
        line: {
          stroke: alpha(theme.palette.divider, 0.3),
          strokeWidth: 1,
        },
      },
      ticks: {
        line: {
          stroke: alpha(theme.palette.divider, 0.3),
          strokeWidth: 1,
        },
        text: {
          fill: theme.palette.text.secondary,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily,
          fontWeight: 500,
        },
      },
      legend: {
        text: {
          fill: theme.palette.text.primary,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily,
          fontWeight: 600,
        },
      },
    },
    grid: {
      line: {
        stroke: alpha(theme.palette.divider, 0.2),
        strokeWidth: 1,
      },
    },
    tooltip: {
      container: {
        background: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: '8px',
        padding: theme.spacing(1.5),
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
        fontSize: '0.8rem',
      },
    },
  }

  const yValues = chartData[0].data.map((d) => (typeof d.y === 'number' ? d.y : 0))
  const yMax = Math.max(0, ...yValues)
  const desiredYTicks = 6
  const yStep = Math.max(1, Math.ceil(yMax / Math.max(1, desiredYTicks - 1)))
  const yAxisTickValues = Array.from({ length: Math.floor(yMax / yStep) + 1 }, (_, i) => i * yStep)

  const commonProps = {
    data: chartData,
    theme: chartTheme,
    // Extra bottom margin for rotated tick labels
    margin: { top: 20, right: 30, bottom: 70, left: 64 },
    enableGridX: true,
    enableGridY: true,
    gridXValues: 4,
    gridYValues: 4,
    axisBottom: {
      tickSize: 0,
      tickPadding: 8,
      // Rotate to prevent collisions
      tickRotation: -35,
      legend: 'Date',
      legendOffset: 52,
      legendPosition: 'middle',
      format: (value) => value,
      tickValues: xAxisTickValues,
      tickTextColor: '#ffffff',
      legendTextColor: '#ffffff',
    },
    axisLeft: {
      tickSize: 0,
      tickPadding: 10,
      tickRotation: 0,
      legend: 'Number of Uploads',
      legendOffset: -60,
      legendPosition: 'middle',
      format: (value) => (typeof value === 'number' && isFinite(value) ? Math.round(value).toLocaleString() : ''),
      tickValues: yAxisTickValues,
      tickTextColor: theme.palette.text.secondary,
      legendTextColor: theme.palette.text.primary,
    },
    enablePoints: true,
    pointSize: 8,
    pointColor: theme.palette.primary.main,
    pointBorderWidth: 3,
    pointBorderColor: theme.palette.background.paper,
    enableArea: true,
    areaOpacity: 0.1,
    useMesh: true,
    legends: [],
    animate: true,
    motionConfig: 'gentle',
  }

  const updateTooltip = (clientX, clientY, dateLabel, countValue) => {
    const containerEl = containerRef.current
    if (!containerEl) return
    const rect = containerEl.getBoundingClientRect()
    const relativeX = clientX - rect.left
    const relativeY = clientY - rect.top
    const tooltipWidth = 200
    const tooltipHeight = 80
    let left = relativeX + 12
    let top = relativeY - tooltipHeight - 12
    if (left + tooltipWidth > rect.width - 8) left = rect.width - tooltipWidth - 8
    if (left < 8) left = 8
    if (top < 8) top = relativeY + 12
    if (top + tooltipHeight > rect.height - 8) top = Math.max(8, rect.height - tooltipHeight - 8)
    setTooltipState({ visible: true, left, top, dateLabel, countValue })
  }

  const hideTooltip = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }))
  }

  // Calculate summary statistics
  const totalUploads = data.daily_trends.reduce((sum, item) => sum + item.count, 0)
  const avgUploads = (totalUploads / data.daily_trends.length).toFixed(1)
  const maxUploads = Math.max(...data.daily_trends.map((item) => item.count))
  const minUploads = Math.min(...data.daily_trends.map((item) => item.count))

  // Calculate trend direction
  const firstHalf = data.daily_trends.slice(0, Math.ceil(data.daily_trends.length / 2))
  const secondHalf = data.daily_trends.slice(Math.floor(data.daily_trends.length / 2))
  const firstHalfAvg = firstHalf.reduce((sum, item) => sum + item.count, 0) / firstHalf.length
  const secondHalfAvg = secondHalf.reduce((sum, item) => sum + item.count, 0) / secondHalf.length
  const trendDirection = secondHalfAvg > firstHalfAvg ? '↗' : secondHalfAvg < firstHalfAvg ? '↘' : '→'
  const trendPercentage = Math.abs(((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100).toFixed(1)

  return (
    <ChartContainer>
      <Box ref={containerRef} sx={{ height: height, width: '100%', position: 'relative' }}>
        {/* Summary Statistics */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              background: alpha(theme.palette.background.paper, 0.9),
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '0.75rem',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Total: <strong>{totalUploads}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Avg: <strong>{avgUploads}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Trend:{' '}
              <strong>
                {trendDirection} {trendPercentage}%
              </strong>
            </Typography>
          </Box>
          <Box
            sx={{
              background: alpha(theme.palette.background.paper, 0.9),
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '0.75rem',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Max: <strong>{maxUploads}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Min: <strong>{minUploads}</strong>
            </Typography>
          </Box>
        </Box>

        {chartType === 'line' ? (
          <ResponsiveLine
            {...commonProps}
            curve="monotoneX"
            lineWidth={3}
            colors={[theme.palette.primary.main]}
            pointSize={8}
            pointColor={theme.palette.primary.main}
            pointBorderWidth={3}
            pointBorderColor={theme.palette.background.paper}
            enableArea={true}
            areaOpacity={0.15}
            areaColor={theme.palette.primary.main}
            animate={true}
            motionConfig="gentle"
            pointTooltip={() => null}
            tooltip={() => null}
            onMouseMove={(point, event) =>
              updateTooltip(event.clientX, event.clientY, point.data.fullDate, point.data.y)
            }
            onMouseLeave={hideTooltip}
          />
        ) : (
          <ResponsiveBar
            {...commonProps}
            colors={[theme.palette.primary.main]}
            borderRadius={4}
            borderWidth={0}
            enableLabel={false}
            animate={true}
            motionConfig="gentle"
            tooltip={() => null}
            onMouseMove={(bar, event) =>
              updateTooltip(
                event.clientX,
                event.clientY,
                (bar.data &&
                  (bar.data.data && bar.data.data.fullDate ? bar.data.data.fullDate : bar.data.indexValue || '')) ||
                  '',
                (bar.data &&
                  (bar.data.data && typeof bar.data.data.y === 'number'
                    ? bar.data.data.y
                    : typeof bar.data.value === 'number'
                    ? bar.data.value
                    : typeof bar.value === 'number'
                    ? bar.value
                    : 0)) ||
                  0,
              )
            }
            onMouseLeave={hideTooltip}
            barComponent={({ bar, ...props }) => (
              <rect {...props} fill={theme.palette.primary.main} stroke={theme.palette.primary.main} strokeWidth={0} />
            )}
          />
        )}
        {tooltipState.visible && (
          <Box
            sx={{
              position: 'absolute',
              left: `${tooltipState.left}px`,
              top: `${tooltipState.top}px`,
              background: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '8px',
              padding: theme.spacing(1.5),
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
              minWidth: '160px',
              maxWidth: '200px',
              zIndex: 20,
              pointerEvents: 'none',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              {tooltipState.dateLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <strong>{tooltipState.countValue}</strong> {tooltipState.countValue === 1 ? 'upload' : 'uploads'}
            </Typography>
          </Box>
        )}
      </Box>
    </ChartContainer>
  )
}

export default UploadTrendsChart
